import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CloudCheck, CircleNotch, WarningCircle, SquaresFour, Table as TableIcon, ChartBar, UploadSimple, Gear } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { computeProject } from "@/lib/calc";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { OverviewTab } from "@/components/project/OverviewTab";
import { BoqTable } from "@/components/project/BoqTable";
import { CategoryTab } from "@/components/project/CategoryTab";
import { UploadPanel } from "@/components/project/UploadPanel";
import { SettingsTab } from "@/components/project/SettingsTab";
import { ExportMenu } from "@/components/project/ExportMenu";

const EDITABLE = ["name", "client", "location", "work_order_no", "default_deduction", "gst_rate", "items"];

function SaveBadge({ state }) {
  const map = {
    saved: [CloudCheck, "Synced", "text-emerald-700"],
    dirty: [CircleNotch, "Unsaved", "text-slate-500"],
    saving: [CircleNotch, "Saving", "text-slate-500"],
    error: [WarningCircle, "Save failed", "text-rose-600"],
  };
  const [Icon, label, cls] = map[state];
  return (
    <span data-testid="save-status" className={`inline-flex items-center gap-1.5 text-xs font-medium ${cls}`}>
      <Icon size={15} className={state === "saving" ? "animate-spin" : ""} weight="bold" /> {label}
    </span>
  );
}

const TABS = [
  ["overview", "Overview", SquaresFour], ["items", "BOQ Items", TableIcon], ["categories", "Categories", ChartBar],
  ["upload", "Upload BOQ", UploadSimple], ["settings", "Settings", Gear],
];

export default function ProjectWorkspace() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("items");
  const [saveState, setSaveState] = useState("saved");
  const ref = useRef(null);
  const timer = useRef(null);
  const dirty = useRef(false);
  const uploading = useRef(false);
  const setUploading = useCallback((v) => { uploading.current = v; }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}`);
      ref.current = data;
      setProject(data);
      setTab((t) => (data.items.length === 0 && t === "items" ? "upload" : t));
    } catch (e) {
      toast.error(errMsg(e, "Could not load project"));
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    if (!dirty.current || !ref.current) return;
    dirty.current = false;
    setSaveState("saving");
    const payload = Object.fromEntries(EDITABLE.map((k) => [k, ref.current[k]]));
    if (!String(payload.name || "").trim()) delete payload.name;
    try {
      const { data } = await api.put(`/projects/${projectId}`, payload);
      if (!dirty.current) {
        ref.current = { ...ref.current, uploads: data.uploads };
        setProject(ref.current);
      }
      setSaveState(dirty.current ? "dirty" : "saved");
    } catch (e) {
      dirty.current = true;
      setSaveState("error");
      toast.error(errMsg(e, "Could not save changes"));
    }
  }, [projectId]);

  const update = useCallback((patch) => {
    if (uploading.current) {
      toast.info("Please wait until the BOQ extraction finishes");
      return;
    }
    const next = { ...ref.current, ...(typeof patch === "function" ? patch(ref.current) : patch) };
    ref.current = next;
    setProject(next);
    dirty.current = true;
    setSaveState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  }, [flush]);

  useEffect(() => {
    const onUnload = (e) => { if (dirty.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onUnload);
    return () => { window.removeEventListener("beforeunload", onUnload); flush(); };
  }, [flush]);

  if (!project) return <FullScreenLoader label="Opening project" />;
  const calc = computeProject(project);

  const deleteUpload = async (sourceId) => {
    await flush();
    try {
      await api.delete(`/projects/${projectId}/uploads/${sourceId}`);
      toast.success("Uploaded BOQ deleted");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not delete upload"));
    }
  };

  return (
    <div className="max-w-[1600px] p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 rise-in">
        <div className="min-w-0">
          <Link to="/projects" data-testid="back-to-projects" className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft size={14} /> All projects
          </Link>
          <h1 data-testid="project-title" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-2 break-words">{project.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
            {project.client && <span>{project.client}</span>}
            {project.location && <span>· {project.location}</span>}
            {project.status === "archived" && <span className="px-2 py-0.5 text-xs bg-slate-200 text-slate-700 rounded-sm">Archived</span>}
            <SaveBadge state={saveState} />
          </div>
        </div>
        <ExportMenu projectId={projectId} beforeExport={flush} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="h-auto p-0 bg-transparent border-b border-slate-200 rounded-none w-full justify-start gap-1">
            {TABS.map(([v, label, Icon]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2.5 text-sm text-slate-500 data-[state=active]:text-slate-900 gap-2">
                <Icon size={16} weight="duotone" /> {label}
                {v === "items" && <span className="ml-1 text-[11px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-sm tabular">{project.items.length}</span>}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent value="overview" className="mt-6"><OverviewTab project={project} calc={calc} onGoto={setTab} /></TabsContent>
        <TabsContent value="items" className="mt-6"><BoqTable project={project} calc={calc} update={update} onGoto={setTab} /></TabsContent>
        <TabsContent value="categories" className="mt-6"><CategoryTab calc={calc} /></TabsContent>
        <TabsContent value="upload" forceMount className="mt-6 data-[state=inactive]:hidden">
          <UploadPanel projectId={projectId} hasItems={project.items.length > 0} uploads={project.uploads || []} onDeleteUpload={deleteUpload} beforeUpload={flush} onBusy={setUploading} onDone={async () => { await load(); setTab("items"); }} />
        </TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsTab project={project} calc={calc} update={update} /></TabsContent>
      </Tabs>
    </div>
  );
}
